import { createUserDisplayName } from '@faro/app-context';
import { useUser } from '@faro/auth';
import { FaroNavigationLink, FaroNavigationLinkProps } from '@faro/design-system';
import { ChatMessageIcon } from '@faro/icons';
import { UserInfo } from '@faro/user-management-model';
import { isEmpty } from 'lodash';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useBeforeunload } from 'react-beforeunload';
import { useSystemConfigQuery } from '../../query';
import { getBaseUrl } from '../../util/getBaseUrl';
import { isBlank } from '../../util/isBlank';
import styles from './ChatButton.module.scss';
import { useIntercomUserData } from './useIntercomUserData';

/**
 * Injects and configures Intercom chat client into the UI.
 *
 * Additional resources for configuring the intercom chat bubble:
 *
 * Via admin ui:
 *  1. https://www.intercom.com/help/en/articles/178-customize-the-intercom-messenger-basics
 *  2. https://www.intercom.com/help/en/articles/189-turn-off-show-or-hide-the-intercom-messenger
 *
 * Via code:
 *  1. https://developers.intercom.com/installing-intercom/docs/intercom-javascript
 *  2. https://www.intercom.com/help/en/articles/2894-customize-the-intercom-messenger-technical
 */
export function ChatButton(props: Omit<FaroNavigationLinkProps, 'label'>): React.JSX.Element | null {
    const { className = '', ...rest } = props;

    const mountedRef = useMountedRef();
    const initializedRef = useRef(false);

    const user = useUser<UserInfo>();
    const { data: systemConfig } = useSystemConfigQuery();
    const intercomApplicationId = systemConfig?.intercom?.applicationid;
    const intercomEnabled = !isBlank(intercomApplicationId);

    const { data: intercomUserData } = useIntercomUserData({
        enabled: intercomEnabled && !isEmpty(user?.roles),
    });

    const [unreadMessageCount, setUnreadMessageCount] = useState(0);

    useBeforeunload(() => {
        closeIntercomSession();
    });

    useEffect(() => {
        if (initializedRef.current || !intercomEnabled || intercomUserData == null) {
            return;
        }

        const load = async () => {
            const { Intercom, onUnreadCountChange } = await import('@intercom/messenger-js-sdk');

            const name = createUserDisplayName(user);
            const basePath = getBaseUrl();
            const environment = `${window.location.hostname}${basePath === '/' ? '' : basePath}`;

            Intercom({
                app_id: intercomApplicationId!,
                name,
                email: user.email,
                user_id: user.id,
                user_hash: intercomUserData.hash,
                environment,
                hide_default_launcher: true,
                custom_launcher_selector: '#custom-intercom-launcher',
                alignment: 'left',
                vertical_padding: 96, // 52
                horizontal_padding: 20, // Minimum is 20
                action_color: 'hsl(220, 100%, 53.9%)',
                background_color: 'hsl(220, 100%, 53.9%)',
            });

            onUnreadCountChange((unreadMessageCount: number) => {
                // Because intercom has no unsubscribe API xO
                if (!mountedRef.current) {
                    return;
                }
                setUnreadMessageCount(unreadMessageCount);
            });
        };

        load();

        return () => {
            closeIntercomSession();
        };
    }, [intercomEnabled, intercomUserData]);

    // Don't display chat button if intercom is disabled
    if (intercomApplicationId == null) {
        return null;
    }

    return (
        <FaroNavigationLink
            {...rest}
            id="custom-intercom-launcher"
            data-automation-id="chat-button"
            className={`
                ${styles.root}
                ${unreadMessageCount > 0 ? styles.unreadMessages : ''}
                ${className}
            `}
            iconLeft={
                unreadMessageCount > 0 ? <div className={styles.badge}>{unreadMessageCount}</div> : <ChatMessageIcon />
            }
            label={<>Chat with us</>}
        />
    );
}

function closeIntercomSession(): void {
    if (typeof Intercom === 'function') {
        Intercom('hide');
        Intercom('shutdown');
    }
}

function useMountedRef(): RefObject<boolean> {
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    return mountedRef;
}
